import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

export interface BreakdownSlice {
  /** Nyers kulcs (faj vagy státusz) — a szín ehhez kötődik. */
  key: string;
  label: string;
  count: number;
}

interface BreakdownDonutProps {
  title: string;
  icon?: React.ReactNode;
  data: BreakdownSlice[];
  loading?: boolean;
  /** Kulcs -> szín. Ami nincs benne, a tartalék palettából kap színt. */
  colors?: Record<string, string>;
  emptyLabel?: string;
}

/** Tartalék paletta — ismeretlen kulcsokhoz, stabil sorrendben. */
const FALLBACK_COLORS = ['#0d9488', '#f59e0b', '#6366f1', '#ec4899', '#84cc16', '#06b6d4', '#f97316', '#8b5cf6'];

/**
 * Megoszlás donut diagramként, mellette olvasható jelmagyarázattal.
 *
 * ── Miért van jelmagyarázat a diagram mellett ──────────────────────────────
 * Egy körcikk mérete önmagában nehezen olvasható: két hasonló szeletről nem
 * lehet megmondani, melyik a nagyobb, a pontos számot pedig végképp nem. A
 * diagram az arányt mutatja meg egy pillantásra, a lista a tényleges számot —
 * a kettő együtt válaszol arra is, hogy "mennyi", és arra is, hogy "mennyivel".
 *
 * ── Miért donut és nem torta ───────────────────────────────────────────────
 * A közepén elfér az összes darabszám, tehát a legfontosabb szám nem külön
 * felirat, hanem a diagram része.
 */
export const BreakdownDonut: React.FC<BreakdownDonutProps> = ({
  title, icon, data, loading = false, colors = {}, emptyLabel = 'Még nincs adat.',
}) => {
  // Csökkenő sorrend: a jelmagyarázat így a legnagyobb tétellel kezdődik,
  // és a szeletek sorrendje is követi az óramutatót.
  const slices = useMemo(
    () => [...data].filter(d => d.count > 0).sort((a, b) => b.count - a.count),
    [data]
  );
  const total = useMemo(() => slices.reduce((s, d) => s + d.count, 0), [slices]);

  const colorFor = (key: string, index: number) =>
    colors[key] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
        {icon}{title}
      </h3>

      {loading ? (
        <div className="h-56 flex items-center justify-center">
          <div className="w-32 h-32 rounded-full border-8 border-gray-100 animate-pulse" />
        </div>
      ) : slices.length === 0 ? (
        <div className="h-56 flex items-center justify-center text-sm text-gray-400">{emptyLabel}</div>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative w-44 h-44 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="count"
                  nameKey="label"
                  innerRadius="62%"
                  outerRadius="100%"
                  paddingAngle={2}
                  stroke="none"
                  isAnimationActive={false}
                >
                  {slices.map((slice, i) => (
                    <Cell key={slice.key} fill={colorFor(slice.key, i)} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any, name: any) => [`${value} db`, name]}
                  contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb', fontSize: '0.8rem' }}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* A középső szám a diagram belsejében — a `pointer-events-none`
                nélkül elnyelné a szeletek hover-eseményeit. */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-bold text-gray-900">{total}</span>
              <span className="text-[11px] text-gray-400">összesen</span>
            </div>
          </div>

          <ul className="flex-1 w-full space-y-1.5">
            {slices.map((slice, i) => (
              <li key={slice.key} className="flex items-center gap-2 text-sm">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colorFor(slice.key, i) }} />
                <span className="text-gray-700 flex-1 truncate">{slice.label}</span>
                <span className="font-semibold text-gray-900">{slice.count}</span>
                <span className="text-xs text-gray-400 w-11 text-right">
                  {total > 0 ? `${Math.round((slice.count / total) * 100)}%` : '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
